import { useState } from "react";

import { useUploadFileToOpenAI, useAttachFileToVectorStore } from "@/queries/vector-store";
import { displayToast, displayErrorToast } from "@/utils/toast";
import { ToastType } from "@/enums";

interface UseFileUploadProps {
  selectedVectorStoreId: string | null;
}

interface UploadingFile {
  name: string;
  size: number;
  tempId: string;
}

export const useFileUpload = ({ selectedVectorStoreId }: UseFileUploadProps) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<Map<string, UploadingFile>>(new Map());

  const { mutateAsync: uploadFileToOpenAI, isLoading: isUploadingFile } = useUploadFileToOpenAI();
  const { mutateAsync: attachFile, isLoading: isAttachingFile } = useAttachFileToVectorStore();

  const SUPPORTED_EXTENSIONS = [
    ".c",
    ".cpp",
    ".cs",
    ".css",
    ".csv",
    ".doc",
    ".docx",
    ".gif",
    ".go",
    ".html",
    ".java",
    ".jpeg",
    ".jpg",
    ".js",
    ".json",
    ".md",
    ".pdf",
    ".php",
    ".pkl",
    ".png",
    ".pptx",
    ".py",
    ".rb",
    ".tar",
    ".tex",
    ".ts",
    ".txt",
    ".webp",
    ".xlsx",
    ".xml",
    ".zip",
  ];

  const validateFileType = (file: File): boolean => {
    const fileName = file.name.toLowerCase();
    return SUPPORTED_EXTENSIONS.some(ext => fileName.endsWith(ext));
  };

  const validateFiles = (files: File[]): File[] => {
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    files.forEach(file => {
      if (validateFileType(file)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      displayErrorToast(
        `Unsupported file type(s): ${invalidFiles.join(
          ", ",
        )}. Supported formats: ${SUPPORTED_EXTENSIONS.join(", ")}`,
      );
    }

    return validFiles;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length > 0) {
      const validFiles = validateFiles(files);
      if (validFiles.length > 0) {
        setSelectedFiles(prev => [...prev, ...validFiles]);
      }
      event.target.value = ""; // Clear input
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const validFiles = validateFiles(files);
      if (validFiles.length > 0) {
        setSelectedFiles(prev => [...prev, ...validFiles]);
      }
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadAndAttach = async () => {
    if (selectedFiles.length === 0 || !selectedVectorStoreId) {
      displayErrorToast("Please select files and a vector store");
      return;
    }

    // Save files to upload before clearing
    const filesToUpload = [...selectedFiles];

    // Add new files to existing uploading files (don't replace)
    const fileToTempIdMap = new Map<File, string>();
    filesToUpload.forEach(file => {
      const tempId = `uploading-${Date.now()}-${Math.random()}`;
      fileToTempIdMap.set(file, tempId);
      setUploadingFiles(prev => {
        const next = new Map(prev);
        next.set(tempId, {
          name: file.name,
          size: file.size,
          tempId,
        });
        return next;
      });
    });
    setSelectedFiles([]); // Clear selected files immediately

    // Track confirmation state for this batch
    const batchSuccessCount = { current: 0 };
    const batchConfirmedCount = { current: 0 };
    const batchFailCount = { current: 0 };

    try {
      for (const file of filesToUpload) {
        const tempId = fileToTempIdMap.get(file);

        try {
          const uploadResult = await uploadFileToOpenAI({
            file: file,
            purpose: "assistants",
          });

          if (!uploadResult?.success || !uploadResult?.file?.id) {
            throw new Error("Failed to upload file to OpenAI");
          }

          batchSuccessCount.current += 1;

          // Attach file and track confirmation
          await attachFile({
            vector_store_id: selectedVectorStoreId,
            file_id: uploadResult.file.id,
            onConfirmed: () => {
              batchConfirmedCount.current += 1;

              // Remove from uploading files once confirmed
              if (tempId) {
                setUploadingFiles(prev => {
                  const next = new Map(prev);
                  next.delete(tempId);
                  return next;
                });
              }

              // Show toast only after all files in this batch are confirmed
              if (
                batchConfirmedCount.current === batchSuccessCount.current &&
                batchSuccessCount.current > 0
              ) {
                displayToast(
                  `Successfully uploaded ${batchSuccessCount.current} file${
                    batchSuccessCount.current > 1 ? "s" : ""
                  }`,
                  ToastType.success,
                );
              }
            },
          });
        } catch (error: unknown) {
          // eslint-disable-next-line no-console
          console.error(`Failed to upload ${file.name}:`, error);
          batchFailCount.current += 1;

          // Remove from uploading files on error
          if (tempId) {
            setUploadingFiles(prev => {
              const next = new Map(prev);
              next.delete(tempId);
              return next;
            });
          }
        }
      }

      // If no files succeeded, we won't get any confirmations, so handle errors immediately
      if (batchSuccessCount.current === 0 && batchFailCount.current > 0) {
        displayErrorToast(
          `Failed to upload ${batchFailCount.current} file${batchFailCount.current > 1 ? "s" : ""}`,
        );
      } else if (batchSuccessCount.current > 0 && batchFailCount.current > 0) {
        // Some succeeded, some failed - show error immediately, success will show when confirmed
        displayErrorToast(
          `Failed to upload ${batchFailCount.current} file${batchFailCount.current > 1 ? "s" : ""}`,
        );
      }
    } catch (error: unknown) {
      const getErrorMessage = (err: unknown, defaultMessage: string): string => {
        if (typeof err === "string") {
          return err;
        }

        if (err instanceof Error) {
          return err.message;
        }
        return defaultMessage;
      };

      const errorMessage = getErrorMessage(error, "Failed to upload files");
      displayErrorToast(errorMessage);
      setUploadingFiles(new Map()); // Clear all uploading files on error
    }
  };

  return {
    selectedFiles,
    isDragOver,
    uploadingFiles: Array.from(uploadingFiles.values()),
    handleFileSelect,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleRemoveFile,
    handleUploadAndAttach,
    isUploading: isUploadingFile || isAttachingFile,
  };
};
